import { z } from 'zod';
import { reminderRepeatKinds } from './recurrence.js';
/**
 * NAPTAR (versenyaudit H1).
 *
 * Egy bejegyzes egy CSATORNA vagy egy FELHASZNALOI CSOPORT naptaraba kerul;
 * mindketto latszik a tagok sajat naptaraban, megjelolve, honnan jott. Lehet
 * sima esemeny vagy hivas; csoportnal a hivas a valasztott csatornaban fut.
 */
export const calendarScopeSchema = z.enum(['channel', 'group']);
// ─── Megbeszeles: valasz, sorozat-hatokor (docs/23) ──────────────────────────
/** Valasz egy meghivora. A SOROZATRA szol; aki nem valaszolt: `null`. */
export const calendarResponseSchema = z.enum(['accepted', 'tentative', 'declined']);
export const CALENDAR_RESPONSE_LABEL = {
    accepted: 'Igen',
    tentative: 'Talán',
    declined: 'Nem',
};
export const calendarRespondInput = z.object({ response: calendarResponseSchema });
/**
 * Ismetlodo sorozat modositasa / torlese - mint az Outlookban: CSAK EZ az alkalom,
 * EZ ES A KOVETKEZOK, vagy az EGESZ sorozat. Egyszeri bejegyzesnel mindig `all`.
 */
export const calendarSeriesScopeSchema = z.enum(['one', 'following', 'all']);
export const CALENDAR_SERIES_SCOPE_LABEL = {
    one: 'Csak ez az alkalom',
    following: 'Ez és az összes következő',
    all: 'Az egész sorozat',
};
/** Modositas (PATCH) hatokore - a query-ben jon, a torzs a `calendarEventInput`. */
export const calendarEditQuery = z.object({
    scope: calendarSeriesScopeSchema.default('all'),
    /** Az alkalom EREDETI kezdete (`one` / `following` eseten kotelezo). */
    occurrence: z.string().datetime().optional(),
});
/** Torles (DELETE): hatokor + kinek. `everyone` = lemondas (csak a szervezo). */
export const calendarDeleteQuery = calendarEditQuery.extend({
    for: z.enum(['me', 'everyone']).default('everyone'),
});
/** Ennyi perccel a kezdes elott szolhat az emlekezteto. `null` = nem szol. */
export const CALENDAR_REMIND_OPTIONS = [0, 5, 10, 15, 30, 60];
export const calendarRepeatSchema = z.object({
    kind: z.enum(reminderRepeatKinds),
    /** Heti ismetlodesnel a napok, 0 = vasarnap. */
    days: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    /** Meddig (ISO), `null` = visszavonasig. */
    until: z.string().datetime().nullable().default(null),
});
export const calendarEventInput = z
    .object({
    scope: calendarScopeSchema,
    /** Csatorna-bejegyzesnel a naptar csatornaja; csoportnal a hivas csatornaja. */
    channelId: z.string().min(1).nullable().default(null),
    groupId: z.string().min(1).nullable().default(null),
    title: z.string().trim().min(1, 'Adj címet a bejegyzésnek.').max(200),
    description: z.string().trim().max(4000).nullable().default(null),
    startsAt: z.string().datetime(),
    durationMinutes: z
        .number()
        .int()
        .min(5)
        .max(7 * 24 * 60),
    allDay: z.boolean().default(false),
    withCall: z.boolean().default(false),
    remindMinutes: z
        .number()
        .int()
        .refine((value) => CALENDAR_REMIND_OPTIONS.includes(value))
        .nullable()
        .default(10),
    repeat: calendarRepeatSchema.default({ kind: 'none', days: [], until: null }),
})
    .refine((value) => (value.scope === 'channel' ? Boolean(value.channelId) : true), {
    message: 'Válassz csatornát.',
    path: ['channelId'],
})
    .refine((value) => (value.scope === 'group' ? Boolean(value.groupId) : true), {
    message: 'Válassz csoportot.',
    path: ['groupId'],
})
    .refine((value) => !(value.scope === 'group' && value.withCall && !value.channelId), {
    message: 'Add meg, melyik csatornában legyen a hívás.',
    path: ['channelId'],
});
/** Egy bejegyzes - ahogy szerkeszteshez visszajon. */
export const calendarEventSchema = z.object({
    id: z.string(),
    scope: calendarScopeSchema,
    channelId: z.string().nullable(),
    groupId: z.string().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    startsAt: z.string(),
    durationMinutes: z.number(),
    allDay: z.boolean(),
    withCall: z.boolean(),
    remindMinutes: z.number().nullable(),
    repeat: calendarRepeatSchema,
    timeZone: z.string(),
    /** A szervezo (a keszito). */
    createdBy: z.string().nullable(),
    /** Szerkesztheti-e a kero - CSAK a szervezo (docs/23). */
    canEdit: z.boolean(),
    /** A kero valasza a meghivora (`null`: nem valaszolt; a szervezonel mindig `null`). */
    myResponse: calendarResponseSchema.nullable(),
    /** A resztvevok (a csatorna / csoport tagjai) es a valaszuk. */
    attendees: z.array(z.object({ userId: z.string(), response: calendarResponseSchema.nullable() })),
});
/** Egy ALKALOM a naptarban - az ismetlodo sorozat kibontva. */
export const calendarOccurrenceSchema = z.object({
    eventId: z.string(),
    start: z.string(),
    end: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    scope: calendarScopeSchema,
    channelId: z.string().nullable(),
    groupId: z.string().nullable(),
    allDay: z.boolean(),
    withCall: z.boolean(),
    recurring: z.boolean(),
    /** Magyar leiras: "minden hetfon", ha ismetlodik. */
    repeatText: z.string().nullable(),
    createdBy: z.string().nullable(),
    canEdit: z.boolean(),
    /**
     * Az alkalom EREDETI kezdete - ez azonositja (modositas, lemondas, "csak
     * nalam"). Atirt alkalomnal elter a `start`-tol.
     */
    occurrenceStart: z.string(),
    /** Ezt az egy alkalmat kulon atirtak (ido, cim vagy leiras). */
    overridden: z.boolean(),
    /** A kero valasza (`null`: nem valaszolt). */
    myResponse: calendarResponseSchema.nullable(),
});
/**
 * A "Naptar" uzenetenek `meta`-ja (a `calendar` altipusu uzenet).
 *
 * PILLANATKEP a kuldes idejebol: a kartya ebbol rajzol, ha a bejegyzes azota
 * megvaltozott vagy elmaradt, akkor is. A valasz-gombok allapota viszont elo -
 * azt a kartya a bejegyzesbol kerdezi.
 */
export const calendarMessageMetaSchema = z.object({
    eventId: z.string(),
    kind: z.enum(['invite', 'update', 'cancel', 'responses']),
    /** Modositasnal / lemondasnal: mire vonatkozik. */
    seriesScope: calendarSeriesScopeSchema.nullable(),
    /** Az erintett alkalom eredeti kezdete (`one` / `following`). */
    occurrenceStart: z.string().nullable(),
    title: z.string(),
    start: z.string(),
    end: z.string(),
    allDay: z.boolean(),
    withCall: z.boolean(),
    scope: calendarScopeSchema,
    channelId: z.string().nullable(),
    groupId: z.string().nullable(),
    repeatText: z.string().nullable(),
    /** Modositasnal a REGI kezdes, ha az idopont valtozott. */
    previousStart: z.string().nullable(),
    organizerId: z.string().nullable(),
});
/** Meddig lehet egyszerre lekerdezni (napban) - egy havi nezet + rahagyas. */
export const CALENDAR_MAX_RANGE_DAYS = 62;
/** Mennyivel a kezdes ELOTT lehet mar csatlakozni egy naptaras hivashoz. */
export const CALENDAR_JOIN_EARLY_MS = 15 * 60_000;
// ─── Outlook-kapcsolat (docs/23, B1) ──────────────────────────────────────────
/** Az Outlook-kapcsolat allapota a Beallitasokhoz. */
export const outlookStatusSchema = z.object({
    /** Be van-e allitva a szerveren (Microsoft-alkalmazas + titkositasi kulcs). */
    enabled: z.boolean(),
    linked: z.boolean(),
    linkedAt: z.string().nullable(),
    syncedAt: z.string().nullable(),
    /** Az utolso szinkron hibaja, emberi mondatban. */
    error: z.string().nullable(),
    /** A Microsoft visszavonta a hozzaferest - ujra ossze kell kapcsolni. */
    needsReauth: z.boolean(),
});
export const outlookShowAsSchema = z.enum([
    'free',
    'tentative',
    'busy',
    'oof',
    'workingElsewhere',
    'unknown',
]);
export const OUTLOOK_SHOW_AS_LABEL = {
    free: 'Szabad',
    tentative: 'Talán',
    busy: 'Foglalt',
    oof: 'Házon kívül',
    workingElsewhere: 'Máshol dolgozik',
    unknown: 'Ismeretlen',
};
/** Egy SAJAT Outlook-esemeny - csak a tulajdonosa latja a reszleteit. */
export const outlookEventSchema = z.object({
    id: z.string(),
    start: z.string(),
    end: z.string(),
    allDay: z.boolean(),
    subject: z.string(),
    location: z.string().nullable(),
    showAs: outlookShowAsSchema,
    /** Privatnak jelolt az Outlookban. */
    isPrivate: z.boolean(),
    joinUrl: z.string().nullable(),
    webLink: z.string().nullable(),
});
/** Egy foglalt sav a kollegaknak - CIM NELKUL. */
export const busySlotSchema = z.object({
    start: z.string(),
    end: z.string(),
    allDay: z.boolean(),
    showAs: outlookShowAsSchema,
});
// ─── Szabad idopont keresese (docs/23) ────────────────────────────────────────
export const calendarFreeSlotsInput = z.object({
    scope: calendarScopeSchema,
    channelId: z.string().min(1).nullable().default(null),
    groupId: z.string().min(1).nullable().default(null),
    /** A nap a kero idozonajaban: 2026-10-21. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    durationMinutes: z
        .number()
        .int()
        .min(5)
        .max(8 * 60),
    /** Modositasnal a sajat bejegyzes ne szamitson foglaltsagnak. */
    excludeEventId: z.string().min(1).optional(),
});
export const freeSlotSchema = z.object({
    start: z.string(),
    end: z.string(),
    /** Hany resztvevo er ra. */
    freeCount: z.number(),
    total: z.number(),
    /** Akik NEM ernek ra (foglaltak, vagy munkaidejukon kivul esik). */
    busyUserIds: z.array(z.string()),
});
export const freeSlotsResultSchema = z.object({
    slots: z.array(freeSlotSchema),
    total: z.number(),
    /** Hany resztvevonek van osszekotott Outlook-naptara (a tobbieket csak az AVSlackbol latjuk). */
    withOutlook: z.number(),
});
//# sourceMappingURL=calendar.js.map